import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../auth/AuthContext';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';

export default function AdminDashboardNews() {
  const { csrfToken } = useAuth();

  const [news, setNews] = useState([]);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState('');
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [tags, setTags] = useState('');
  const [status, setStatus] = useState('draft');
  const [scheduleDate, setScheduleDate] = useState('');
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const [previewUrl, setPreviewUrl] = useState(null);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [editingNews, setEditingNews] = useState(null);

  const editor = useEditor({
    extensions: [StarterKit],
    content: '',
  });

  // Load news
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get('/api/admin/news', {
          headers: { 'X-CSRF-Token': csrfToken },
          withCredentials: true,
        });
        setNews(Array.isArray(data) ? data : data.news || []);
      } catch {
        setError('Error loading news');
      }
    })();
  }, [csrfToken]);

  // Load categories
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get('/api/categories');
        setCategories(data);
        if (data.length > 0) setCategory(data[0].name);
      } catch {
        console.error('Error loading categories');
      }
    })();
  }, []);

  // Auto-clear messages
  useEffect(() => {
    if (message || error) {
      const t = setTimeout(() => {
        setMessage(null);
        setError(null);
      }, 3000);
      return () => clearTimeout(t);
    }
  }, [message, error]);

  // Upload thumbnail to Cloudinary
  const handleThumbnailUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setPreviewUrl(URL.createObjectURL(file));

    const formData = new FormData();
    formData.append('file', file);

    try {
      const { data } = await axios.post('/api/news/upload-thumbnail', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });

      setThumbnailUrl(data.url);
    } catch (err) {
      console.error('Thumbnail upload error:', err);
      setError('Error uploading thumbnail');
    }
  };

  // Add news
  async function addNews(e) {
    e.preventDefault();
    setError(null);
    setMessage(null);

    try {
      const content = editor.getHTML();

      const payload = {
        title,
        summary,
        content,
        category,
        tags,
        status,
        scheduleDate,
        thumbnailUrl,
      };

      const { data } = await axios.post('/api/admin/news', payload, {
        headers: { 'X-CSRF-Token': csrfToken },
        withCredentials: true,
      });

      setNews(prev => [data.news || data, ...prev]);
      setMessage('News added successfully!');
      resetForm();
    } catch {
      setError('Error adding news');
    }
  }

  // Start editing
  function startEdit(n) {
    setEditingNews(n);
    setTitle(n.title);
    setSummary(n.summary);
    editor?.commands.setContent(n.content || '');
    setCategory(n.category);
    setTags((n.tags || []).join(', '));
    setStatus(n.status);
    setScheduleDate(n.scheduleDate ? n.scheduleDate.slice(0, 16) : '');
    setThumbnailUrl(n.thumbnailUrl || '');
    setPreviewUrl(n.thumbnailUrl || null);
  }

  // Update news
  async function updateNews(e) {
    e.preventDefault();
    if (!editingNews) return;

    try {
      const content = editor.getHTML();

      const payload = {
        title,
        summary,
        content,
        category,
        tags,
        status,
        scheduleDate,
        thumbnailUrl,
      };

      const { data } = await axios.put(`/api/admin/news/${editingNews._id}`, payload, {
        headers: { 'X-CSRF-Token': csrfToken },
        withCredentials: true,
      });

      setNews(prev => prev.map(n => (n._id === data._id ? data : n)));
      setMessage('News updated successfully!');
      resetForm();
    } catch {
      setError('Error updating news');
    }
  }

  // Delete news
  async function deleteNews(id) {
    try {
      await axios.delete(`/api/admin/news/${id}`, {
        headers: { 'X-CSRF-Token': csrfToken },
        withCredentials: true,
      });
      setNews(prev => prev.filter(n => n._id !== id));
      setMessage('News deleted');
    } catch {
      setError('Error deleting news');
    }
  }

  // Reset form
  function resetForm() {
    setTitle('');
    setSummary('');
    setTags('');
    setStatus('draft');
    setScheduleDate('');
    setThumbnailUrl('');
    setPreviewUrl(null);
    editor?.commands.setContent('');
    setEditingNews(null);
  }

  return (
    <div className="container py-5">
      <h1 className="h4 mb-4">Manage Tech News</h1>

      {message && <div className="alert alert-success">{message}</div>}
      {error && <div className="alert alert-danger">{error}</div>}

      <form onSubmit={editingNews ? updateNews : addNews} className="card p-4 shadow-sm mb-4">

        {/* Title */}
        <div className="mb-3">
          <label className="form-label">Title</label>
          <input className="form-control" value={title} onChange={e => setTitle(e.target.value)} required />
        </div>

        {/* Summary */}
        <div className="mb-3">
          <label className="form-label">Summary</label>
          <input className="form-control" value={summary} onChange={e => setSummary(e.target.value)} />
        </div>

        {/* Content */}
        <div className="mb-3">
          <label className="form-label">Content</label>
          <EditorContent editor={editor} className="border p-2 rounded" />
        </div>

        {/* Thumbnail */}
        <div className="mb-3">
          <label className="form-label">Thumbnail</label>
          <input type="file" className="form-control" onChange={handleThumbnailUpload} />
          {previewUrl && (
            <img src={previewUrl} alt="Preview" className="img-fluid rounded mt-2" style={{ maxHeight: '200px' }} />
          )}
        </div>

        {/* Category */}
        <div className="mb-3">
          <label className="form-label">Category</label>
          <select className="form-select" value={category} onChange={e => setCategory(e.target.value)}>
            {categories.map(c => <option key={c._id} value={c.name}>{c.name}</option>)}
          </select>
        </div>

        {/* Tags */}
        <div className="mb-3">
          <label className="form-label">Tags</label>
          <input className="form-control" value={tags} onChange={e => setTags(e.target.value)} />
        </div>

        {/* Status */}
        <div className="mb-3">
          <label className="form-label">Status</label>
          <select className="form-select" value={status} onChange={e => setStatus(e.target.value)}>
            <option value="draft">Draft</option>
            <option value="published">Publish Now</option>
          </select>
        </div>

        {/* Schedule */}
        <div className="mb-3">
          <label className="form-label">Schedule Date</label>
          <input type="datetime-local" className="form-control" value={scheduleDate} onChange={e => setScheduleDate(e.target.value)} />
        </div>

        <button className="btn btn-success">{editingNews ? 'Update News' : 'Add News'}</button>
      </form>

      {/* News list */}
      <ul className="list-group">
        {news.map(n => (
          <li key={n._id} className="list-group-item d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              {n.thumbnailUrl && (
                <img src={n.thumbnailUrl} alt={n.title} style={{ width: 56, height: 56, objectFit: 'cover' }} className="rounded me-3" />
              )}
              <div>
                <strong>{n.title}</strong>
                <div className="text-muted">{n.category}</div>
              </div>
            </div>

            <div>
              <button className="btn btn-warning btn-sm me-2" onClick={() => startEdit(n)}>Edit</button>
              <button className="btn btn-danger btn-sm" onClick={() => deleteNews(n._id)}>Delete</button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
